import {
  AuctionBiddingStatus,
  AuctionResponse,
  AuctionType,
  LotStatus,
  PersonResponse,
  SaleStatus,
} from '@bonhams1793/bonhams-typescript'
import { RefObject, useState } from 'react'
import styled, { css } from 'styled-components'

import { LotResponse } from '@nx/api'
import {
  HelpIcon,
  PieChartIcon,
  PriceTagIcon,
  ShippingIcon,
} from '@nx/fire/assets'
import { getTranslate } from '@nx/translations'
import { useUserInteraction } from '@nx/utils/google-tag-manager'

import { AuctionViewingsButton } from '@web/components/common/AuctionViewingsButton'

import { ActionPanelItem } from '../ActionPanel/ActionPanelItem'
import { CostCalculatorModal } from '../CostCalculator'
import { DownloadConditionsReport } from '../DownloadConditionsReport'
import { StyledHowToBuyLink } from './ActionPanel.styles'
import translate from './ActionPanel.translate.json'

const { text } = getTranslate(translate)
const showCostCalculatorAuctions = [
  // Initial test
  28237, 28239, 28240, 28241, 28980, 28981, 28982, 28983, 28984, 28985, 28986,
  29089, 29091, 29092, 29093, 29094, 29095, 29096, 29097, 29098, 29099, 29100,
  29101, 29102, 29103,
  // 2024 + 2025
  29716, 29717, 29718, 29719, 29720, 29721, 30998, 30999, 31000, 31002, 31003,
  31005, 31006, 31007, 31008, 31009, 31011, 31013, 31015, 31017, 31019, 31022,
  31023, 31024, 31025, 31027, 31030, 31047, 31048, 31049, 31050, 31052, 31053,
  31054, 31055, 31056, 31057, 31058, 31059, 31060, 31062, 31063, 31064, 31065,
]

export function ActionPanel({
  auction,
  lot,
  contacts,
  auctionViewingsData,
}: ActionPanelProps) {
  const showCostCalculatorCTA = showCostCalculatorAuctions.includes(
    lot.iSaleNo.iSaleNo
  )
  const { recordInteraction } = useUserInteraction()

  const [showCostCalculatorModal, setShowCalculatorModal] = useState(false)

  const showHowToBid =
    auction.sSaleType === AuctionType.online
      ? auction.sBidOnlineStatus === AuctionBiddingStatus.inProgress
      : auction.sSaleStatus === SaleStatus.ready &&
        auction.sBidOnlineStatus !== AuctionBiddingStatus.ended
  const showConditionsReport =
    lot.sLotStatus === LotStatus.new &&
    ((lot.iSaleLotNoUnique && lot.has_condition_report) ||
      (auction.lOfferCondition && auction.sSaleStatus !== SaleStatus.finished))

  return showHowToBid ||
    lot.shipping_quote_link ||
    showConditionsReport ||
    showCostCalculatorCTA ||
    auctionViewingsData ? (
    <ActionPanelContainer>
      {showHowToBid && (
        <ActionPanelItem
          href={
            auction.sSaleType === AuctionType.online
              ? '/how-to-buy/how-to-bid-online/'
              : '/how-to-buy/how-to-bid-live/'
          }
          icon={HelpIcon}
          title={text('howToBid')}
        />
      )}
      {lot.sLotStatus !== LotStatus.withdrawn && lot.shipping_quote_link && (
        <ActionPanelItem
          href={lot.shipping_quote_link}
          target="_blank"
          rel="noreferrer"
          icon={ShippingIcon}
          title={text('shippingQuote')}
        />
      )}
      {showCostCalculatorCTA && (
        <>
          <CostCalculatorModal
            isOpen={showCostCalculatorModal}
            setIsOpen={setShowCalculatorModal}
            auction={auction}
            lot={lot}
          />

          <ActionPanelItem
            id="cost-calculator-cta"
            icon={PieChartIcon}
            title={text('totalCostCalculator')}
            onClick={() => {
              recordInteraction({
                action: 'costCalculator.click',
                label: `${auction.iSaleNo}-${lot.lPreviewLot ? lot.iSaleLotNoUnique : lot.iSaleLotNo}`,
                category: '',
              })
              setShowCalculatorModal(
                (showCostCalculatorModal) => !showCostCalculatorModal
              )
            }}
          />
        </>
      )}
      {showConditionsReport && (
        <DownloadConditionsReport
          auction={auction}
          lot={lot}
          contacts={contacts}
        />
      )}
      {auctionViewingsData && (
        <StyledAuctionViewingsButton
          auctionViewingsRef={auctionViewingsData?.ref}
          auction={auction}
        />
      )}
      <StyledHowToBuyLink
        startIcon={PriceTagIcon}
        variant="ghost"
        href="/how-to-buy/"
      >
        {text('howtobuy')}
      </StyledHowToBuyLink>
    </ActionPanelContainer>
  ) : null
}

const ActionPanelContainer = styled.div`
  ${({ theme }) => css`
    ${theme.helpers.bottomSeparationLine}
    ${theme.helpers.topSeparationLine}
    margin-top: 30px;
    padding-bottom: 15px;
    padding-top: 30px;

    > *:not(:last-child) {
      margin-right: 40px;
    }

    > {
      button,
      a {
        text-decoration: underline;
      }
    }

    a > span {
      text-decoration: inherit;
      cursor: pointer;
    }
  `}
`

const StyledAuctionViewingsButton = styled(AuctionViewingsButton)`
  svg {
    width: 22px;
    height: 22px;
    margin-right: 4px;
  }
`

export interface ActionPanelProps {
  auction: AuctionResponse
  lot: LotResponse
  contacts: PersonResponse[]
  auctionViewingsData?: { ref: RefObject<HTMLButtonElement> }
}
